// RegisterPage_refactored v1: 重构后的注册页面主组件
// 从 RegisterPage.js 重构而来，使用模块化组件和自定义Hook

import React from 'react';

import { RegisterHeader, RegisterForm, RegisterFooter } from './components';
import { useRegisterForm, useRegisterActions } from './hooks';
import AnimatedShaderBackground from '../ui/AnimatedShaderBackground';
import FluidGeometryBackground from '../ui/FluidGeometryBackground';
import RotatingCube from '../ui/RotatingCube';
import AudioPlayer from '../ui/AudioPlayer';

// ============================================================================
// 背景效果切换配置 (可逆测试)
// ============================================================================
// 设置为 true 使用新的流体几何形状效果
// 设置为 false 使用原来的流星效果
// ============================================================================
const USE_FLUID_GEOMETRY_EFFECT = true; // 切换这个值来测试不同效果

/**
 * RegisterPage_refactored 组件 - 重构后的注册页面
 * @returns {JSX.Element} 注册页面
 */
const RegisterPage_refactored = () => {
  // 使用自定义Hook管理表单状态
  const {
    formData,
    errors,
    isLoading,
    isSendingCode,
    handleInputChange,
    validateForm,
    setLoading,
    setSendingCode,
    setError,
    setGeneralError,
  } = useRegisterForm();

  // 使用自定义Hook管理操作
  const actions = useRegisterActions({
    formData,
    validateForm,
    setLoading,
    setSendingCode,
    setError,
    setGeneralError,
    onStepChange: step => {
      // 這裡可以處理步驟變化
      console.log('Step changed to:', step);
    },
  });

  return (
    <div className='min-h-[106.3vh] bg-gray-950 flex flex-col relative'>
      {/* 背景動畫 - 深層背景 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
        {USE_FLUID_GEOMETRY_EFFECT ? (
          <FluidGeometryBackground />
        ) : (
          <AnimatedShaderBackground />
        )}
      </div>

      {/* 旋轉立方體動畫 - 淺層裝飾 */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1 }}>
        <RotatingCube />
      </div>

      {/* 音頻播放器 - 右上角 */}
      <AudioPlayer />

      {/* Main Content - 添加 relative z-10 確保在所有背景動畫上方 */}
      <div className='relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-6'>
          {/* Register Card */}
          <div className='bg-transparent rounded-lg p-8'>
            {/* Header Section */}
            <RegisterHeader />

            {/* Form Section */}
            <RegisterForm
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
              onSendCode={actions.sendVerificationCode}
              onSubmit={actions.handleSubmit}
              isLoading={isLoading}
              isSendingCode={isSendingCode}
            />

            {/* Footer Section */}
            <RegisterFooter onLoginClick={actions.handleLoginClick} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className='bg-gradient-to-r from-blue-500 to-purple-600 hidden md:block relative z-10'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
          <div className='flex justify-between items-center'>
            <div className='text-sm text-white'>
              <span className='font-bold'>TAG Art Guide</span>
              <span className='ml-2'>© 2025 TAG. All rights reserved.</span>
            </div>
            <div className='flex space-x-6 text-sm text-white'>
              <a
                href='/privacy'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Privacy Policy
              </a>
              <a
                href='/terms'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Terms of Service
              </a>
              <a
                href='/help'
                target='_blank'
                rel='noopener noreferrer'
                className='hover:text-gray-200'
              >
                Help Center
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RegisterPage_refactored;
